import type { EnemyDef } from '../core/combat';
import type { Piece } from '../core/types';

/**
 * The pieces enemies drop into the well. Shaped like the player's cards — a
 * tetromino of blocks — so an enemy turn is legible as "it put a piece there",
 * not an invisible stat change.
 *
 * Two flavours, per the design: coloured attack pieces (mixed kinds, so the
 * enemy has to read the board and complete a two-run) and black blocker pieces
 * (all Stone) that simply occupy the squares you were about to use.
 */
/** Shardling's attack piece — ember-heavy so the AI scores match completions. */
const P_SHARD_S: Piece = {
  cells: [[0, 1], [0, 2], [1, 0], [1, 1]],
  kinds: ['ember', 'ember', 'frost', 'ember'],
};
/** Prism Wisp's prismatic disruption piece — all four colors at once. */
const P_PRISM_T: Piece = {
  cells: [[0, 0], [0, 1], [0, 2], [1, 1]],
  kinds: ['ember', 'frost', 'venom', 'volt'],
};
const P_T: Piece = { cells: [[0, 0], [0, 1], [0, 2], [1, 1]], kinds: ['venom', 'ember', 'venom', 'volt'] };
const P_L: Piece = { cells: [[0, 0], [1, 0], [2, 0], [2, 1]], kinds: ['ember', 'frost', 'ember', 'venom'] };
const P_I4: Piece = { cells: [[0, 0], [1, 0], [2, 0], [3, 0]], kinds: ['ember', 'frost', 'ember', 'frost'] };
const P_BLOCK_O: Piece = { cells: [[0, 0], [0, 1], [1, 0], [1, 1]], kinds: ['stone', 'stone', 'stone', 'stone'] };
const P_BLOCK_I: Piece = { cells: [[0, 0], [1, 0], [2, 0], [3, 0]], kinds: ['stone', 'stone', 'stone', 'stone'] };

/**
 * The opportunist. Shardling reads YOUR board and plays against you — its
 * ember-heavy piece lands where the AI scores match completions, and its
 * addStone turn scatters junk into whatever setup you were building.
 */
export const SHARDLING: EnemyDef = {
  id: 'shardling',
  name: 'Shardling',
  maxHp: 22,
  pattern: [
    { kind: 'dropPiece', piece: P_SHARD_S },
    { kind: 'attack', amount: 6 },
    { kind: 'addStone', count: 2 },
    { kind: 'attackTimes', amount: 4, hits: 2 },
  ],
};

/**
 * The constructor. Golem builds persistent garbage the player must deal with —
 * a holed stone row, scattered stones, a structured blocker — before they can
 * focus on damage. Every turn adds to the mess.
 */
export const JUNK_GOLEM: EnemyDef = {
  id: 'junk_golem',
  name: 'Junk Golem',
  maxHp: 30,
  pattern: [
    { kind: 'pushUpStone', rows: 1 },
    { kind: 'multi', intents: [{ kind: 'attack', amount: 6 }, { kind: 'addStone', count: 2 }] },
    { kind: 'dropPiece', piece: P_BLOCK_O },
    { kind: 'attack', amount: 6 },
  ],
};

/**
 * The rearranger. Wisp scrambles your board and drops pieces that create color
 * chaos. It disrupts more than it damages — the scramble IS the threat, and
 * one turn per cycle is pure board disruption with no attack at all.
 */
export const PRISM_WISP: EnemyDef = {
  id: 'prism_wisp',
  name: 'Prism Wisp',
  maxHp: 23,
  pattern: [
    { kind: 'multi', intents: [{ kind: 'scramble', count: 6 }, { kind: 'dropPiece', piece: P_PRISM_T }] },
    { kind: 'scramble', count: 4 },
    { kind: 'multi', intents: [{ kind: 'attack', amount: 8 }, { kind: 'dropPiece', piece: P_T }] },
  ],
};

/**
 * The hexer. Sage turns your gems to Stone and defends itself behind a wall of
 * Block. Two hexColumn turns in a three-turn cycle make the petrification
 * identity unmistakable — after two turns the player knows what Sage does.
 */
export const CRYSTAL_SAGE: EnemyDef = {
  id: 'crystal_sage',
  name: 'Crystal Sage',
  maxHp: 54,
  pattern: [
    { kind: 'multi', intents: [{ kind: 'block', amount: 12 }, { kind: 'hexColumn' }] },
    { kind: 'hexColumn' },
    { kind: 'multi', intents: [{ kind: 'attack', amount: 10 }, { kind: 'dropPiece', piece: P_L }] },
  ],
};

/**
 * The doom clock. Mason escalates: turn 1 is a warning (moderate attack +
 * floor rises), turn 2 tightens (heavier attack + strategic piece), turn 3 is
 * the hammer (stone wall + floor rises twice). The escalation is the identity.
 */
export const THE_MASON: EnemyDef = {
  id: 'the_mason',
  name: 'The Mason',
  maxHp: 100,
  pattern: [
    { kind: 'multi', intents: [{ kind: 'attack', amount: 8 }, { kind: 'pushUpStone', rows: 1 }] },
    { kind: 'multi', intents: [{ kind: 'attack', amount: 12 }, { kind: 'dropPiece', piece: P_I4 }] },
    // Drop first: a drop resolves the board, so a push-up raised in the same
    // intent would be swept away by the enemy's own cascade.
    { kind: 'multi', intents: [{ kind: 'dropPiece', piece: P_BLOCK_I }, { kind: 'pushUpStone', rows: 2 }] },
  ],
};

export const ALL_ENEMIES: readonly EnemyDef[] = [
  SHARDLING,
  JUNK_GOLEM,
  PRISM_WISP,
  CRYSTAL_SAGE,
  THE_MASON,
];
